import useStore from '@data/state/zustand';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import translate from '@data/translations';
import { DayPickerLocale } from '@pages/newReservations/reservations/DateTimeSelector';
import { createPortal } from 'react-dom';

// Ručni parser — ne koristi dayjs plugin da ne bi pokvarivao globalni dayjs
const parseDateByFormat = (input: string, format: string): Date | null => {
    const sep = format.replace(/[A-Z]/gi, '').charAt(0);
    if (!sep) return null;
    const inputParts = input.split(sep);
    const formatParts = format.split(sep);
    if (inputParts.length !== 3 || formatParts.length !== 3) return null;
    const get = (key: string): number => {
        const idx = formatParts.indexOf(key);
        return idx >= 0 ? parseInt(inputParts[idx], 10) : NaN;
    };
    const day = get('DD');
    const month = get('MM') - 1;
    const year = get('YYYY');
    if (isNaN(day) || isNaN(month + 1) || isNaN(year) || year < 1000) return null;
    const date = new Date(year, month, day);
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
    return date;
};

interface DayPickerProps {
    value: number | null;
    onSelect: Function;
    fromYear?: number;
    toYear?: number;
    disabled?: boolean;
    disabledBefore?: Date;
    disabledAfter?: Date;
    ref?: any;
    width?: string;
    height?: string;
    fontSize?: string;
    usePortal?: boolean;
    allowManualInput?: boolean;
}

export const CustomDayPicker = (props: DayPickerProps) => {
    const { value, onSelect, fromYear, toYear, disabled, disabledBefore, disabledAfter, ref, usePortal, allowManualInput = false } = props;
    const [activePicker, setActivePicker] = useState(false);
    const pickerRef = React.useRef<HTMLDivElement>(null);
    const otherSettings = useStore.getState().otherSettings;
    const locale = useStore.getState().locale;
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const dateFormat = otherSettings?.dateFormat || 'DD/MM/YYYY';
    const [inputValue, setInputValue] = useState(value ? dayjs(value).format(dateFormat) : '');
    const isFocused = React.useRef(false);

    React.useEffect(() => {
        if (!isFocused.current) {
            setInputValue(value ? dayjs(value).format(dateFormat) : '');
        }
    }, [value]);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                setActivePicker(false);
            }
        };

        if (activePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activePicker]);

    const disabledProps =
        disabledBefore && disabledAfter
            ? { before: disabledBefore, after: disabledAfter }
            : disabledBefore
            ? { before: disabledBefore }
            : disabledAfter
            ? { after: disabledAfter }
            : undefined;

    const calendar = (
        <div
            style={
                usePortal
                    ? {
                          top: pos.top,
                          left: pos.left,
                      }
                    : {}
            }
            className="absolute z-99999 bg-white shadow p-2"
            ref={ref}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <DayPicker
                disabled={disabled ? disabled : disabledProps ? disabledProps : false}
                mode="single"
                selected={value ? new Date(value) : undefined}
                defaultMonth={value ? new Date(value) : new Date()}
                onSelect={(date) => {
                    if (!date) return; // klik na isti dan -> maknuto selektiranje
                    onSelect(date);
                    setActivePicker(false);
                }}
                locale={DayPickerLocale[locale] ?? undefined}
                captionLayout={fromYear && toYear ? 'dropdown-buttons' : undefined}
                fromYear={fromYear ?? undefined}
                toYear={toYear ?? undefined}
                modifiersClassNames={{
                    start: 'custom-selected',
                    end: 'custom-selected',
                    range: 'custom-range',
                    today: 'custom-today',
                }}
                footer={
                    <div className="flex-center-center" style={{ textAlign: 'center', marginTop: '8px' }}>
                        <div
                            onClick={(e: any) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onSelect(new Date());
                                setActivePicker(false);
                            }}
                            className="button-info w-30"
                        >
                            {translate('Today')}
                        </div>
                    </div>
                }
            />
        </div>
    );

    return (
        <div ref={pickerRef} className="dayPickerLight">
            <input
                ref={inputRef}
                type="text"
                placeholder={dateFormat}
                disabled={disabled}
                value={inputValue}
                readOnly={!allowManualInput}
                onFocus={() => {
                    isFocused.current = true;
                }}
                onBlur={() => {
                    isFocused.current = false;
                    const parsed = parseDateByFormat(inputValue, dateFormat);
                    const isDisabled = !parsed ||
                        (disabledBefore ? parsed < disabledBefore : false) ||
                        (disabledAfter ? parsed > disabledAfter : false);
                    if (isDisabled) {
                        setInputValue(value ? dayjs(value).format(dateFormat) : '');
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        isFocused.current = false;
                        const parsed = parseDateByFormat(inputValue, dateFormat);
                        const isDisabled = !parsed ||
                            (disabledBefore ? parsed < disabledBefore : false) ||
                            (disabledAfter ? parsed > disabledAfter : false);
                        if (isDisabled) {
                            setInputValue(value ? dayjs(value).format(dateFormat) : '');
                        }
                        inputRef.current?.blur();
                    }
                }}
                onChange={(e) => {
                    const typed = e.target.value;
                    setInputValue(typed);
                    const parsed = parseDateByFormat(typed, dateFormat);
                    if (parsed) {
                        if (disabledBefore && parsed < disabledBefore) return;
                        if (disabledAfter && parsed > disabledAfter) return;
                        onSelect(parsed);
                    }
                }}
                onClick={() => {
                    setActivePicker(!activePicker);
                    if (inputRef?.current) {
                        const rect = inputRef.current.getBoundingClientRect();
                        setPos({
                            top: rect.bottom + window.scrollY,
                            left: rect.left + window.scrollX,
                        });
                    }
                }}
                className="date-picker-input-custom"
                style={{ width: props.width || '', height: props.height || '', fontSize: props.fontSize || '' }}
            />
            {activePicker ? (usePortal ? createPortal(calendar, document.body) : calendar) : null}
        </div>
    );
};
